import { useEffect, useState, useMemo } from "react";
import { Plus, Search, Upload, Download, Edit, Trash2, X, Camera, Tag, Layers, Star, Package, ChevronRight, FolderEdit } from "lucide-react";
import api, { formatErr, fileUrl } from "../lib/apiClient";
import { fmtCAD } from "../lib/format";

const TIERS = [
  { key: "good", label: "Good", color: "bg-slate-100 text-slate-700", ring: "ring-slate-300" },
  { key: "better", label: "Better", color: "bg-sky-100 text-sky-800", ring: "ring-sky-300" },
  { key: "best", label: "Best", color: "bg-amber-100 text-amber-900", ring: "ring-amber-300" },
];

const imgUrl = (id) => fileUrl(`/pricebook/${id}/image`);

export default function PriceBook() {
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [search, setSearch] = useState("");
  const [activeCat, setActiveCat] = useState("all");
  const [activeSub, setActiveSub] = useState(null);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [showCatMgr, setShowCatMgr] = useState(false);
  const [err, setErr] = useState("");

  const load = async () => {
    const params = new URLSearchParams();
    if (search) params.set("search", search);
    if (activeCat !== "all") params.set("category", activeCat);
    const [{ data: items }, { data: catData }] = await Promise.all([
      api.get(`/pricebook?${params}`),
      api.get("/pricebook/categories"),
    ]);
    setItems(items);
    setCats(catData);
  };
  useEffect(() => { const t = setTimeout(load, 150); return () => clearTimeout(t); }, [search, activeCat]);

  const filtered = useMemo(() => {
    return items.filter((it) => !activeSub || (it.subcategory || "") === activeSub);
  }, [items, activeSub]);

  // Group by variant_group so Good/Better/Best sibling sets render together
  const grouped = useMemo(() => {
    const groups = new Map();
    for (const it of filtered) {
      const key = it.variant_group || `_solo:${it.id}`;
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(it);
    }
    const order = { good: 0, better: 1, best: 2, "": 3 };
    return [...groups.entries()].map(([k, arr]) => ({
      key: k,
      isVariantSet: !k.startsWith("_solo:") && arr.length > 1,
      items: arr.sort((a, b) => (order[a.variant_tier || ""] - order[b.variant_tier || ""])),
    }));
  }, [filtered]);

  const blank = { name: "", description: "", category: "Services", subcategory: "", cost_price: 0, sale_price: 0, unit: "ea", taxable: true, variant_group: "", variant_tier: "", internal_labour_cost: 0, internal_material_cost: 0, material_markup_percent: 0 };

  const save = async (e) => {
    e.preventDefault(); setErr("");
    try {
      const payload = {
        ...editing,
        cost_price: Number(editing.cost_price),
        sale_price: Number(editing.sale_price),
        internal_labour_cost: Number(editing.internal_labour_cost || 0),
        internal_material_cost: Number(editing.internal_material_cost || 0),
        material_markup_percent: Number(editing.material_markup_percent || 0),
      };
      let item;
      if (editing.id) {
        ({ data: item } = await api.put(`/pricebook/${editing.id}`, payload));
      } else {
        ({ data: item } = await api.post("/pricebook", payload));
      }
      // Image upload (if a File was attached during edit)
      if (editing._imageFile) {
        const fd = new FormData(); fd.append("file", editing._imageFile);
        await api.post(`/pricebook/${item.id}/image`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      setShowForm(false); setEditing(null);
      load();
    } catch (e) { setErr(formatErr(e)); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this item?")) return;
    await api.delete(`/pricebook/${id}`); load();
  };

  const importCsv = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/pricebook/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      alert(`${data.imported} items imported (${data.skipped || 0} skipped)`); load();
    } catch (e) { alert(formatErr(e)); }
    e.target.value = "";
  };

  return (
    <div className="space-y-5" data-testid="pricebook-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">Price Book</h1>
          <p className="text-sm text-slate-500 mt-1">Services and materials grouped into Good / Better / Best options for one-tap quoting.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setShowCatMgr(true)} data-testid="manage-categories-btn" className="px-3 py-2 border border-slate-300 text-sm rounded-md hover:bg-slate-50 flex items-center gap-1.5"><FolderEdit size={14}/>Categories</button>
          <label data-testid="import-pricebook-btn" className="px-3 py-2 border border-slate-300 text-sm rounded-md hover:bg-slate-50 cursor-pointer flex items-center gap-1.5"><Upload size={14}/>Import CSV
            <input type="file" accept=".csv" className="hidden" onChange={importCsv} />
          </label>
          <a href={fileUrl("/pricebook/export")} className="px-3 py-2 border border-slate-300 text-sm rounded-md hover:bg-slate-50 flex items-center gap-1.5"><Download size={14}/>Export</a>
          <button onClick={() => { setEditing({ ...blank }); setShowForm(true); }} data-testid="new-item-btn" className="px-3 py-2 bg-[#0F4C81] hover:bg-[#0A365C] text-white text-sm rounded-md font-medium flex items-center gap-1.5"><Plus size={14}/>New item</button>
        </div>
      </div>

      <div className="grid lg:grid-cols-[240px_1fr] gap-5">
        {/* Sidebar — categories tree */}
        <aside data-testid="pricebook-sidebar" className="lg:sticky lg:top-20 self-start bg-white border border-slate-200 rounded-md p-2 max-h-[80vh] overflow-y-auto">
          <button
            onClick={() => { setActiveCat("all"); setActiveSub(null); }}
            className={`w-full text-left px-3 py-2 rounded text-sm font-medium flex items-center justify-between ${activeCat === "all" ? "bg-[#0F4C81] text-white" : "hover:bg-slate-50 text-slate-700"}`}
            data-testid="cat-all"
          >
            <span className="flex items-center gap-2"><Layers size={14}/> All items</span>
            <span className="text-xs opacity-75">{items.length}</span>
          </button>
          {cats.map((cat) => (
            <div key={cat.name} className="mt-1">
              <button
                onClick={() => { setActiveCat(cat.name); setActiveSub(null); }}
                className={`w-full text-left px-3 py-2 rounded text-sm flex items-center justify-between ${activeCat === cat.name && !activeSub ? "bg-[#0F4C81] text-white font-medium" : "hover:bg-slate-50 text-slate-700"}`}
                data-testid={`cat-${cat.name}`}
              >
                <span className="flex items-center gap-2"><Tag size={13}/> {cat.name}</span>
                <span className="text-xs opacity-75">{cat.count}</span>
              </button>
              {activeCat === cat.name && cat.subcategories?.length > 0 && (
                <ul className="ml-3 mt-0.5 border-l border-slate-200">
                  {cat.subcategories.map((sc) => (
                    <li key={sc.name}>
                      <button
                        onClick={() => setActiveSub(activeSub === sc.name ? null : sc.name)}
                        className={`w-full text-left pl-3 pr-2 py-1.5 text-xs flex items-center justify-between ${activeSub === sc.name ? "bg-slate-100 text-[#0F4C81] font-semibold" : "hover:bg-slate-50 text-slate-600"}`}
                        data-testid={`sub-${sc.name}`}
                      >
                        <span className="flex items-center gap-1.5">{activeSub === sc.name && <ChevronRight size={10}/>} {sc.name}</span>
                        <span className="text-[10px]">{sc.count}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </aside>

        {/* Items grid */}
        <div>
          <div className="bg-white border border-slate-200 rounded-md p-3 mb-4 flex items-center gap-2">
            <Search size={16} className="text-slate-400 ml-1"/>
            <input data-testid="pricebook-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder={`Search ${activeCat === "all" ? "all items" : activeCat.toLowerCase()}…`} className="flex-1 text-sm bg-transparent focus:outline-none"/>
            <span className="text-xs text-slate-400 mr-1">{filtered.length} item{filtered.length !== 1 ? "s" : ""}</span>
          </div>

          {grouped.length === 0 && (
            <div className="bg-white border border-dashed border-slate-300 rounded-md p-12 text-center text-sm text-slate-500">
              <Package size={28} className="mx-auto text-slate-300 mb-3"/>
              No items in {activeCat === "all" ? "your price book" : `"${activeCat}"`} yet.
              <div className="mt-3"><button onClick={() => { setEditing({ ...blank, category: activeCat === "all" ? "Services" : activeCat }); setShowForm(true); }} className="text-[#0F4C81] hover:underline font-medium">+ Add your first item</button></div>
            </div>
          )}

          <div className="space-y-4">
            {grouped.map((g) => g.isVariantSet ? (
              <VariantGroupCard key={g.key} group={g} onEdit={(it) => { setEditing(it); setShowForm(true); }} onDelete={del} />
            ) : (
              <SoloItemCard key={g.key} item={g.items[0]} onEdit={(it) => { setEditing(it); setShowForm(true); }} onDelete={del} />
            ))}
          </div>
        </div>
      </div>

      {showForm && (
        <ItemFormModal
          item={editing}
          setItem={setEditing}
          onClose={() => { setShowForm(false); setEditing(null); }}
          onSave={save}
          err={err}
          existingCats={cats}
        />
      )}

      {showCatMgr && (
        <CategoryManagerModal
          cats={cats}
          onClose={() => setShowCatMgr(false)}
          onChanged={() => load()}
        />
      )}
    </div>
  );
}

/* ---------- Category Manager ---------- */

function CategoryManagerModal({ cats, onClose, onChanged }) {
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [newCat, setNewCat] = useState("");

  const renameCat = async (cat) => {
    const newName = window.prompt(`Rename category "${cat.name}" to:`, cat.name);
    if (!newName || newName === cat.name) return;
    setBusy(true); setErr("");
    try {
      await api.post("/pricebook/categories/rename", { old_name: cat.name, new_name: newName });
      onChanged();
    } catch (e) { setErr(formatErr(e)); } finally { setBusy(false); }
  };

  const deleteCat = async (cat) => {
    const keep = window.confirm(`Delete category "${cat.name}"?\n\nOK = move its ${cat.count} item(s) to "Uncategorized"\nCancel = abort`);
    if (!keep) return;
    setBusy(true); setErr("");
    try {
      await api.post("/pricebook/categories/delete", { name: cat.name, delete_items: false });
      onChanged();
    } catch (e) { setErr(formatErr(e)); } finally { setBusy(false); }
  };

  const renameSub = async (cat, sub) => {
    const newName = window.prompt(`Rename subcategory "${sub.name}" (in ${cat.name}) to:`, sub.name);
    if (!newName || newName === sub.name) return;
    setBusy(true); setErr("");
    try {
      await api.post("/pricebook/categories/rename", {
        old_name: cat.name, new_name: cat.name,
        subcategory: sub.name, new_subcategory: newName,
      });
      onChanged();
    } catch (e) { setErr(formatErr(e)); } finally { setBusy(false); }
  };

  const deleteSub = async (cat, sub) => {
    if (!window.confirm(`Clear subcategory "${sub.name}" from its ${sub.count} item(s)?`)) return;
    setBusy(true); setErr("");
    try {
      await api.post("/pricebook/categories/delete", { name: cat.name, subcategory: sub.name, delete_items: false });
      onChanged();
    } catch (e) { setErr(formatErr(e)); } finally { setBusy(false); }
  };

  const addCategory = async () => {
    const name = newCat.trim();
    if (!name) return;
    setBusy(true); setErr("");
    try {
      // Create a placeholder item to materialise the category, then user can edit/delete it.
      await api.post("/pricebook", {
        name: `${name} — Placeholder`,
        description: "Edit or delete me. I was created to materialise this category.",
        category: name, subcategory: "",
        cost_price: 0, sale_price: 0, unit: "ea", taxable: true,
        variant_group: "", variant_tier: "",
      });
      setNewCat("");
      onChanged();
    } catch (e) { setErr(formatErr(e)); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => !busy && onClose()}>
      <div onClick={(e) => e.stopPropagation()} data-testid="category-manager-modal" className="bg-white rounded-lg w-full max-w-lg shadow-xl flex flex-col max-h-[85vh]">
        <header className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FolderEdit size={16} className="text-[#0F4C81]"/>
            <h2 className="font-display text-xl font-bold text-slate-900">Manage categories</h2>
          </div>
          <button onClick={onClose} disabled={busy} className="p-1 text-slate-400 hover:text-slate-900 rounded"><X size={18}/></button>
        </header>
        <div className="px-5 py-4 border-b border-slate-100">
          <label className="text-xs font-medium uppercase tracking-wider text-slate-500">New category</label>
          <div className="flex gap-2 mt-1">
            <input
              data-testid="new-category-input"
              value={newCat}
              onChange={(e) => setNewCat(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addCategory()}
              placeholder="e.g. Heating"
              className="flex-1 px-3 py-2 text-sm border border-slate-300 rounded focus:outline-none focus:border-[#0F4C81]"
            />
            <button type="button" onClick={addCategory} disabled={busy || !newCat.trim()} data-testid="add-category-btn" className="px-3 py-2 bg-[#0F4C81] text-white text-sm rounded hover:bg-[#0A365C] disabled:opacity-50 flex items-center gap-1.5">
              <Plus size={13}/> Add
            </button>
          </div>
        </div>
        {err && <div className="px-5 py-2 text-sm text-red-700 bg-red-50 border-b border-red-100">{err}</div>}
        <div className="flex-1 overflow-y-auto px-5 py-3">
          {cats.length === 0 && <div className="text-center text-sm text-slate-400 py-8">No categories yet.</div>}
          {cats.map((cat) => (
            <div key={cat.name} data-testid={`catmgr-cat-${cat.name}`} className="py-3 border-b border-slate-100 last:border-b-0">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Tag size={13} className="text-slate-400"/>
                  <span className="font-medium text-sm text-slate-900">{cat.name}</span>
                  <span className="text-[10px] uppercase text-slate-400 bg-slate-50 px-1.5 py-0.5 rounded">{cat.count} item{cat.count !== 1 ? "s" : ""}</span>
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={() => renameCat(cat)} disabled={busy} data-testid={`rename-cat-${cat.name}`} className="p-1.5 text-slate-400 hover:text-slate-900 hover:bg-slate-100 rounded" title="Rename"><Edit size={13}/></button>
                  <button onClick={() => deleteCat(cat)} disabled={busy} data-testid={`delete-cat-${cat.name}`} className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded" title="Delete (moves items to Uncategorized)"><Trash2 size={13}/></button>
                </div>
              </div>
              {cat.subcategories?.length > 0 && (
                <ul className="ml-5 mt-2 space-y-1">
                  {cat.subcategories.map((sub) => (
                    <li key={sub.name} className="flex items-center justify-between text-xs">
                      <span className="text-slate-600 flex items-center gap-1.5"><ChevronRight size={11} className="text-slate-300"/>{sub.name} <span className="text-slate-400">({sub.count})</span></span>
                      <div className="flex items-center gap-1">
                        <button onClick={() => renameSub(cat, sub)} disabled={busy} data-testid={`rename-sub-${sub.name}`} className="p-1 text-slate-400 hover:text-slate-900 hover:bg-slate-100 rounded"><Edit size={11}/></button>
                        <button onClick={() => deleteSub(cat, sub)} disabled={busy} data-testid={`delete-sub-${sub.name}`} className="p-1 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded"><Trash2 size={11}/></button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
        <footer className="px-5 py-3 border-t border-slate-100 flex justify-end">
          <button onClick={onClose} disabled={busy} className="px-4 py-2 border border-slate-300 text-sm rounded-md hover:bg-slate-50 disabled:opacity-50">Done</button>
        </footer>
      </div>
    </div>
  );
}

/* ---------- Cards ---------- */

function SoloItemCard({ item, onEdit, onDelete }) {
  return (
    <div data-testid={`pricebook-item-${item.id}`} className="bg-white border border-slate-200 rounded-md p-4 flex gap-4 hover:border-slate-300">
      <Thumb item={item}/>
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="font-semibold text-slate-900 truncate">{item.name}</div>
            {item.description && <div className="text-xs text-slate-500 mt-0.5 line-clamp-2">{item.description}</div>}
            <div className="flex items-center gap-2 mt-2 text-[11px]">
              <span className="text-slate-500">{item.category}{item.subcategory && ` › ${item.subcategory}`}</span>
              {item.taxable === false && <span className="bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">No tax</span>}
            </div>
          </div>
          <div className="text-right shrink-0">
            <div className="text-xs text-slate-500 line-through tabular">{fmtCAD(item.cost_price)}</div>
            <div className="font-display text-lg font-bold tabular text-slate-900">{fmtCAD(item.sale_price)}</div>
            <div className="text-[10px] text-slate-500 uppercase tracking-wider">per {item.unit}</div>
          </div>
        </div>
      </div>
      <RowActions item={item} onEdit={onEdit} onDelete={onDelete}/>
    </div>
  );
}

function VariantGroupCard({ group, onEdit, onDelete }) {
  const groupName = group.items.find((i) => i.variant_tier === "good")?.name?.split(" — ")[0]
                  || group.items[0].name.split(" — ")[0];
  return (
    <div data-testid={`pricebook-variant-${group.key}`} className="bg-white border border-slate-200 rounded-md overflow-hidden">
      <div className="px-4 py-3 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Star size={14} className="text-amber-500"/>
          <h3 className="font-semibold text-slate-900 text-sm">{groupName}</h3>
          <span className="text-[10px] uppercase tracking-wider text-slate-500 font-bold bg-white border border-slate-200 px-1.5 py-0.5 rounded">{group.items.length}-tier package</span>
        </div>
        <div className="text-xs text-slate-500">{group.items[0].category}{group.items[0].subcategory && ` › ${group.items[0].subcategory}`}</div>
      </div>
      <div className="grid sm:grid-cols-3 divide-x divide-slate-200">
        {TIERS.map((t) => {
          const it = group.items.find((i) => i.variant_tier === t.key);
          if (!it) return <div key={t.key} className="p-4 bg-slate-50/40 text-center text-xs text-slate-400 italic">No {t.label} tier</div>;
          return (
            <div key={t.key} data-testid={`pricebook-item-${it.id}`} className="p-4 group hover:bg-slate-50 transition-colors relative">
              <div className="flex justify-between items-start mb-2">
                <span className={`text-[10px] uppercase tracking-wider font-bold ${t.color} px-2 py-0.5 rounded`}>{t.label}</span>
                <RowActions item={it} onEdit={onEdit} onDelete={onDelete} small/>
              </div>
              <div className="flex gap-3">
                <Thumb item={it} sm/>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm text-slate-900 truncate">{it.name.replace(/^.* — /, "")}</div>
                  {it.description && <div className="text-xs text-slate-500 mt-0.5 line-clamp-2">{it.description}</div>}
                  <div className="mt-2 font-display text-lg font-bold tabular text-slate-900">{fmtCAD(it.sale_price)}</div>
                  <div className="text-[10px] text-slate-500 uppercase tracking-wider">per {it.unit}</div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Thumb({ item, sm }) {
  const size = sm ? "w-14 h-14" : "w-20 h-20";
  if (item.image_path) {
    return <img src={imgUrl(item.id)} alt="" className={`${size} object-cover rounded border border-slate-200 shrink-0`} loading="lazy" />;
  }
  return (
    <div className={`${size} shrink-0 rounded bg-slate-100 border border-slate-200 flex items-center justify-center text-slate-300`}>
      <Package size={sm ? 18 : 24}/>
    </div>
  );
}

function MarginPreview({ item }) {
  const labour = Number(item.internal_labour_cost || 0);
  const material = Number(item.internal_material_cost || 0);
  const markup = Number(item.material_markup_percent || 0);
  const sale = Number(item.sale_price || 0);
  const cost = labour + material;
  const suggested = material * (1 + markup / 100) + labour;
  const profit = sale - cost;
  const marginPct = sale > 0 ? (profit / sale) * 100 : 0;
  const color = marginPct >= 60 ? "text-emerald-700" : marginPct >= 30 ? "text-amber-700" : "text-rose-700";
  return (
    <div data-testid="pricebook-margin-preview" className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2 text-[11px]">
      <div className="bg-white border border-slate-200 rounded p-2">
        <div className="text-slate-500 uppercase tracking-wider text-[9px]">Suggested price</div>
        <div className="font-bold tabular text-slate-900">{fmtCAD(suggested)}</div>
      </div>
      <div className="bg-white border border-slate-200 rounded p-2">
        <div className="text-slate-500 uppercase tracking-wider text-[9px]">Internal cost</div>
        <div className="font-bold tabular text-slate-900">{fmtCAD(cost)}</div>
      </div>
      <div className="bg-white border border-slate-200 rounded p-2">
        <div className="text-slate-500 uppercase tracking-wider text-[9px]">Profit / unit</div>
        <div className={`font-bold tabular ${profit >= 0 ? "text-emerald-700" : "text-rose-700"}`}>{fmtCAD(profit)}</div>
      </div>
      <div className="bg-white border border-slate-200 rounded p-2">
        <div className="text-slate-500 uppercase tracking-wider text-[9px]">Margin</div>
        <div className={`font-bold tabular ${color}`}>{marginPct.toFixed(1)}%</div>
      </div>
    </div>
  );
}

function RowActions({ item, onEdit, onDelete, small }) {
  const size = small ? 11 : 13;
  return (
    <div className={`flex gap-1 ${small ? "" : "shrink-0"}`}>
      <button onClick={() => onEdit(item)} data-testid={`edit-pricebook-${item.id}`} className="p-1.5 text-slate-400 hover:text-slate-900 hover:bg-slate-100 rounded"><Edit size={size}/></button>
      <button onClick={() => onDelete(item.id)} data-testid={`delete-pricebook-${item.id}`} className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded"><Trash2 size={size}/></button>
    </div>
  );
}

/* ---------- Form modal ---------- */

function ItemFormModal({ item, setItem, onClose, onSave, err, existingCats }) {
  const imgPreview = item._imageFile
    ? URL.createObjectURL(item._imageFile)
    : (item.id && item.image_path ? imgUrl(item.id) : null);
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <form onSubmit={onSave} onClick={(e) => e.stopPropagation()} data-testid="pricebook-form" className="bg-white rounded-lg w-full max-w-2xl shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <h2 className="font-display text-xl font-bold text-slate-900">{item.id ? "Edit item" : "New price book item"}</h2>
          <button type="button" onClick={onClose} className="p-1 text-slate-400 hover:text-slate-900 rounded"><X size={18}/></button>
        </div>
        <div className="px-6 py-5 space-y-4">
          {err && <div className="text-sm text-red-700 bg-red-50 border border-red-200 px-3 py-2 rounded">{err}</div>}

          <div className="grid sm:grid-cols-[120px_1fr] gap-4">
            <div>
              <label className="text-xs font-medium uppercase tracking-wider text-slate-700">Image</label>
              <label data-testid="pricebook-image-upload" className="mt-1 w-full aspect-square border-2 border-dashed border-slate-300 rounded-md flex items-center justify-center cursor-pointer hover:border-[#0F4C81] overflow-hidden bg-slate-50">
                {imgPreview ? <img src={imgPreview} alt="" className="w-full h-full object-cover"/> : <Camera size={20} className="text-slate-400"/>}
                <input type="file" accept="image/*" className="hidden" onChange={(e) => setItem({ ...item, _imageFile: e.target.files?.[0] })}/>
              </label>
              {imgPreview && <button type="button" onClick={() => setItem({ ...item, _imageFile: null, image_path: "" })} className="text-[11px] text-slate-500 hover:text-red-600 mt-1 underline">Remove</button>}
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-medium uppercase tracking-wider text-slate-700">Name</label>
                <input data-testid="pricebook-name" required value={item.name} onChange={(e) => setItem({ ...item, name: e.target.value })} placeholder="Toilet install — Standard" className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded focus:outline-none focus:border-[#0F4C81]"/>
              </div>
              <div>
                <label className="text-xs font-medium uppercase tracking-wider text-slate-700">Description</label>
                <textarea value={item.description || ""} onChange={(e) => setItem({ ...item, description: e.target.value })} rows={5} className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded focus:outline-none focus:border-[#0F4C81] resize-y"/>
              </div>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium uppercase tracking-wider text-slate-700">Category</label>
              <input
                data-testid="pricebook-category"
                value={item.category || ""}
                onChange={(e) => setItem({ ...item, category: e.target.value })}
                list="pb-cat-list"
                placeholder="e.g. Plumbing"
                className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded focus:outline-none focus:border-[#0F4C81]"
              />
              <datalist id="pb-cat-list">{(existingCats || []).map((c) => <option key={c.name} value={c.name}/>)}</datalist>
            </div>
            <div>
              <label className="text-xs font-medium uppercase tracking-wider text-slate-700">Subcategory</label>
              <input
                data-testid="pricebook-subcategory"
                value={item.subcategory || ""}
                onChange={(e) => setItem({ ...item, subcategory: e.target.value })}
                list="pb-sub-list"
                placeholder="e.g. Toilet install"
                className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded focus:outline-none focus:border-[#0F4C81]"
              />
              <datalist id="pb-sub-list">
                {(existingCats || []).flatMap((c) => c.subcategories?.map((s) => <option key={`${c.name}-${s.name}`} value={s.name}/>) || [])}
              </datalist>
            </div>
          </div>

          <div className="grid sm:grid-cols-3 gap-3">
            <div>
              <label className="text-xs font-medium uppercase tracking-wider text-slate-700">Cost price</label>
              <input data-testid="pricebook-cost" type="number" step="0.01" value={item.cost_price} onChange={(e) => setItem({ ...item, cost_price: e.target.value })} className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded focus:outline-none focus:border-[#0F4C81] tabular"/>
            </div>
            <div>
              <label className="text-xs font-medium uppercase tracking-wider text-slate-700">Sale price</label>
              <input data-testid="pricebook-price" required type="number" step="0.01" value={item.sale_price} onChange={(e) => setItem({ ...item, sale_price: e.target.value })} className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded focus:outline-none focus:border-[#0F4C81] tabular"/>
            </div>
            <div>
              <label className="text-xs font-medium uppercase tracking-wider text-slate-700">Unit</label>
              <input value={item.unit || "ea"} onChange={(e) => setItem({ ...item, unit: e.target.value })} className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded focus:outline-none focus:border-[#0F4C81]"/>
            </div>
          </div>

          <div className="border border-amber-200 rounded-md p-3 bg-amber-50/40">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-[10px] uppercase tracking-wider font-bold bg-amber-100 text-amber-900 px-1.5 py-0.5 rounded">Internal — admin only</span>
              <span className="text-xs uppercase tracking-wider font-semibold text-slate-700">Job cost & margin</span>
            </div>
            <p className="text-[11px] text-slate-600 mb-3">These values <b>never</b> appear on customer invoices, estimates, or the portal. They drive your profit-margin reports.</p>
            <div className="grid sm:grid-cols-3 gap-3">
              <div>
                <label className="text-xs font-medium text-slate-700">Labour cost ($)</label>
                <input
                  data-testid="pricebook-internal-labour"
                  type="number" step="0.01"
                  value={item.internal_labour_cost || 0}
                  onChange={(e) => setItem({ ...item, internal_labour_cost: e.target.value })}
                  className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded bg-white focus:outline-none focus:border-[#0F4C81] tabular"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-700">Material cost ($)</label>
                <input
                  data-testid="pricebook-internal-material"
                  type="number" step="0.01"
                  value={item.internal_material_cost || 0}
                  onChange={(e) => setItem({ ...item, internal_material_cost: e.target.value })}
                  className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded bg-white focus:outline-none focus:border-[#0F4C81] tabular"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-700">Material markup (%)</label>
                <input
                  data-testid="pricebook-internal-markup"
                  type="number" step="0.01"
                  value={item.material_markup_percent || 0}
                  onChange={(e) => setItem({ ...item, material_markup_percent: e.target.value })}
                  className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded bg-white focus:outline-none focus:border-[#0F4C81] tabular"
                />
              </div>
            </div>
            <MarginPreview item={item}/>
          </div>

          <div className="border border-slate-200 rounded-md p-3 bg-slate-50/60">
            <div className="flex items-center gap-2 mb-3"><Star size={14} className="text-amber-500"/><span className="text-xs uppercase tracking-wider font-semibold text-slate-700">Good / Better / Best tier (optional)</span></div>
            <p className="text-xs text-slate-500 mb-3">Group up to 3 items together as tiered package options. Customers see them side-by-side; pick whichever fits their budget.</p>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-slate-700">Variant group ID</label>
                <input
                  data-testid="pricebook-variant-group"
                  value={item.variant_group || ""}
                  onChange={(e) => setItem({ ...item, variant_group: e.target.value })}
                  placeholder="e.g. toilet-install"
                  className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded bg-white focus:outline-none focus:border-[#0F4C81]"
                />
                <div className="text-[10px] text-slate-500 mt-1">Items sharing this id are grouped on the listing.</div>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-700">This item is the…</label>
                <select
                  data-testid="pricebook-variant-tier"
                  value={item.variant_tier || ""}
                  onChange={(e) => setItem({ ...item, variant_tier: e.target.value })}
                  className="mt-1 w-full px-3 py-2 text-sm border border-slate-300 rounded bg-white focus:outline-none focus:border-[#0F4C81]"
                >
                  <option value="">— Standalone item —</option>
                  <option value="good">Good (lowest tier)</option>
                  <option value="better">Better (mid tier)</option>
                  <option value="best">Best (highest tier)</option>
                </select>
              </div>
            </div>
          </div>

          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={item.taxable !== false} onChange={(e) => setItem({ ...item, taxable: e.target.checked })} className="rounded"/>
            <span className="text-slate-700">HST applies to this item</span>
          </label>
        </div>
        <div className="px-6 py-3 border-t border-slate-100 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-4 py-2 border border-slate-300 text-sm rounded-md hover:bg-slate-50">Cancel</button>
          <button type="submit" data-testid="save-pricebook-btn" className="px-5 py-2 bg-[#0F4C81] hover:bg-[#0A365C] text-white text-sm rounded-md font-medium flex items-center gap-2"><Plus size={14}/>{item.id ? "Save changes" : "Create item"}</button>
        </div>
      </form>
    </div>
  );
}
